/**
 * API Client - HTTP client for Guardrail API
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function removeStorage(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // no-op: storage may be unavailable in strict privacy modes
  }
}

/**
 * Canonical localStorage token reader — returns the active JWT access token only.
 * The web app always authenticates with JWTs; the stored API key in 'guardrail_api_key'
 * is the user's programmatic key for CLI/MCP use and is never used as a web session token.
 * Non-hook modules (queryFns, api clients) call this; React components use useAuth().token.
 */
export function getStoredToken(): string | undefined {
  return readStorage('guardrail_access_token') || undefined;
}

/**
 * Registered by AuthContext on mount. Returns true if a new token was obtained.
 * Prevents concurrent refresh storms via a shared promise.
 */
let _refreshCallback: (() => Promise<boolean>) | null = null;
let _pendingRefresh: Promise<boolean> | null = null;

export function setAuthRefreshCallback(cb: () => Promise<boolean>): void {
  _refreshCallback = cb;
}

/** Safe localStorage read — returns null in private/restricted browser contexts. */
export { readStorage };

/** Attempt silent token refresh; serialises concurrent callers. */
async function tryRefresh(): Promise<boolean> {
  if (!_refreshCallback) return false;
  if (!_pendingRefresh) {
    _pendingRefresh = _refreshCallback().finally(() => {
      _pendingRefresh = null;
    });
  }
  return _pendingRefresh;
}

/**
 * Deferred logout state.
 *
 * Instead of immediately clearing credentials and redirecting on the first 401,
 * we schedule a logout after a short grace window. During that window the
 * AuthContext proactive-refresh timer (or a concurrent retry from api.request)
 * may restore a valid token, in which case cancelAuthFailure() suppresses the
 * logout entirely. This prevents transient Keycloak unavailability or a brief
 * network hiccup from logging the user out mid-session.
 */
const LOGOUT_GRACE_MS = 5_000;
let _logoutTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Cancel any pending deferred logout.
 * Called by AuthContext whenever it successfully stores a fresh token so that
 * a concurrent in-flight 401 does not cause an unnecessary logout.
 */
export function cancelAuthFailure(): void {
  if (_logoutTimer) {
    clearTimeout(_logoutTimer);
    _logoutTimer = null;
    if (import.meta.env.DEV) console.log('[Auth] Deferred logout cancelled — auth recovered.');
  }
}

/** Check whether the stored access token is currently valid (not expired). */
function storedTokenIsValid(): boolean {
  const stored = readStorage('guardrail_access_token');
  if (!stored) return false;
  try {
    const b64 = stored.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(b64)) as { exp?: number };
    // Require at least 10 s of remaining validity to count as "recovered".
    return !!exp && exp * 1000 > Date.now() + 10_000;
  } catch {
    return false;
  }
}

/**
 * Handle authentication/authorization failures globally.
 *
 * Schedules a deferred logout (LOGOUT_GRACE_MS). If, during that window, auth
 * recovers (proactive refresh, concurrent retry) the pending logout is cancelled
 * via cancelAuthFailure(). Only one timer runs at a time — concurrent 401s from
 * parallel requests (e.g., the scan-poll Promise.all) are coalesced.
 */
export function handleAuthFailure() {
  // A logout is already pending — don't stack another timer.
  if (_logoutTimer) return;

  console.warn(
    `[Auth] Auth failure detected — scheduling deferred logout in ${LOGOUT_GRACE_MS} ms.`
  );

  _logoutTimer = setTimeout(() => {
    _logoutTimer = null;

    // Last-chance check: if the token recovered (e.g., via proactive refresh),
    // suppress the logout so the user stays signed in.
    if (storedTokenIsValid()) {
      if (import.meta.env.DEV) console.log('[Auth] Token recovered before grace period expired — suppressing logout.');
      return;
    }

    console.warn('[Auth] Auth failure confirmed — clearing session and redirecting to /login.');
    // Only clear session tokens — preserve the user's generated API key ('guardrail_api_key')
    // since that is their programmatic credential for CLI/MCP use, not a web session token.
    removeStorage('guardrail_access_token');
    removeStorage('guardrail_user');
    removeStorage('guardrail_user_email');

    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }, LOGOUT_GRACE_MS);
}

/**
 * Check a fetch response for auth errors and redirect appropriately.
 * - 401 → clear credentials, redirect to /login
 * - 403 → authenticated but not authorized for the attempted action;
 *          do NOT clear session globally.
 * - 402 → let the caller handle it (feature gate / payment required);
 *         do NOT redirect globally — free users on the dashboard would be
 *         incorrectly bounced to /pricing when hitting a gated feature.
 */
export function checkAuthResponse(response: Response): Response {
  if (response.status === 401) {
    handleAuthFailure();
  }
  return response;
}

function buildHeaders(options?: {
  body?: unknown;
  token?: string;
  extraHeaders?: Record<string, string>;
}): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options?.extraHeaders,
  };
  if (options?.token) {
    if (options.token.startsWith('gr_') || options.token.startsWith('sk_')) {
      headers['X-API-Key'] = options.token;
    } else {
      headers['Authorization'] = `Bearer ${options.token}`;
    }
  }
  return headers;
}

export async function request<T>(
  method: string,
  path: string,
  options?: {
    body?: unknown;
    token?: string;
    extraHeaders?: Record<string, string>;
  }
): Promise<T> {
  const parseResponsePayload = async (res: Response): Promise<unknown> => {
    if (res.status === 204) return null;

    const contentType = res.headers.get('content-type') || '';
    const raw = await res.text().catch(() => '');
    if (!raw) return null;

    if (contentType.includes('application/json')) {
      try {
        return JSON.parse(raw);
      } catch {
        return raw;
      }
    }

    // Be forgiving when servers forget JSON content-type headers.
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  };

  const body = options?.body ? JSON.stringify(options.body) : undefined;
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: buildHeaders(options),
      body,
    });
  } catch (error) {
    const reason = error instanceof Error ? error.message : 'Unknown network error';
    throw new Error(`Network request failed: ${reason}`, { cause: error });
  }

  // On 401: attempt silent token refresh once, then retry the original request.
  // Falls back to full logout only if refresh also fails.
  if (response.status === 401) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      // Pull the fresh token from storage and rebuild the authorization header.
      const freshToken = readStorage('guardrail_access_token');
      try {
        response = await fetch(`${API_BASE_URL}${path}`, {
          method,
          headers: buildHeaders({ ...options, token: freshToken ?? options?.token }),
          body,
        });
      } catch (error) {
        const reason = error instanceof Error ? error.message : 'Unknown network error';
        throw new Error(`Network request retry failed: ${reason}`, { cause: error });
      }
    }
  }

  checkAuthResponse(response);

  const payload = await parseResponsePayload(response);
  if (import.meta.env.DEV) console.debug(`[API] ${method} ${path} → ${response.status}`, payload);

  if (!response.ok) {
    const message =
      payload &&
      typeof payload === 'object' &&
      'message' in payload &&
      typeof (payload as { message?: unknown }).message === 'string'
        ? (payload as { message: string }).message
        : response.statusText;
    throw new Error(message || `HTTP ${response.status}`);
  }

  return payload as T;
}

export const apiClient = {
  getMe(authToken: string) {
    return request<{
      user: {
        email: string;
        name?: string;
        id: string;
        apiKey: string;
        githubToken?: string; // Saved GitHub PAT from Keycloak attributes
        subscription?: string;
        status?: string;
        stripeCustomerId?: string;
      };
      subscription?: {
        /** Lowercase slug: 'trial' | 'starter' | 'pro' | 'team' | 'enterprise' */
        tier: string;
        /** Human-readable display name from the API, e.g. "Pro", "Starter". Prefer this over tier for display. */
        tierName?: string;
        /** Stripe subscription status: 'active' | 'trialing' | 'canceled' | 'past_due' | … */
        status?: string;
        subscriptionId?: string;
        trialEndsAt?: string;
        /** ISO date set once when the user's first trial is activated; never cleared. Used to prevent re-trial during promo. */
        trialUsedAt?: string;
        periodEnd?: string;
        currentPeriodEnd?: string;
        /** Seat count from product.metadata.seats; -1 = unlimited, null = not set */
        seats?: number | null;
        /** Upcoming invoice details (next payment after trial/period ends) */
        upcomingInvoice?: {
          amountDue: number;
          dueDate?: string;
        };
      };
      limits?: {
        scansRemaining: number;
        scansUsed: number;
        scansPerMonth: number;
      };
      /** Active Stripe subscriptions, populated by useUserInfo from /v2/billing/subscription */
      subscriptions?: {
        id: string;
        tier: string;
        tierName: string;
        status: string;
        periodEnd: string;
      }[];
    }>('GET', '/v2/me', { token: authToken });
  },

  saveGitHubToken(token: string | null, authToken: string) {
    return request<{ success: boolean }>('PATCH', '/v2/me/github-token', {
      token: authToken,
      body: { token },
    });
  },

  /** Update user's display name (firstName / lastName) in Keycloak. */
  updateProfile(firstName: string, lastName: string | undefined, authToken: string) {
    return request<{ success: boolean; name: string }>('PATCH', '/v2/me', {
      token: authToken,
      body: { firstName, lastName },
    });
  },

  /** Start direct GitHub OAuth flow; returns authorization URL and linked status. */
  getGitHubConnect(authToken: string) {
    return request<{ url: string; isLinked: boolean; githubUsername: string | null }>(
      'GET',
      '/v2/github/connect',
      { token: authToken }
    );
  },

  /** Read the saved GitHub token from Keycloak attributes (called after OAuth callback). */
  fetchGitHubToken(authToken: string) {
    return request<{ token: string }>('GET', '/v2/github/token', { token: authToken });
  },

  getPricing() {
    return request<{
      tiers: Array<{
        tier: string;
        name: string;
        /** Stripe product.description */
        description?: string;
        monthlyPrice: number;
        yearlyPrice: number;
        yearlyDiscount: number;
        /** Days of Stripe-native trial from product.metadata.trial_days */
        trialDays?: number;
        features: Array<{ name: string; included: boolean | string | number }>;
        popular?: boolean;
        ctaText: string;
        ctaUrl: string;
        stripeProductId?: string;
        stripePriceIdMonthly?: string;
        stripePriceIdYearly?: string;
        /** Display sort order from product.metadata.order (lower = first) */
        order?: number;
        /** Whether this tier is publicly visible; false = hidden in prod, shown with warning in sandbox */
        active?: boolean;
        /** Sandbox only: Stripe metadata keys the API expected but did not find */
        missingMetadata?: string[];
        /** Seat count from product.metadata.seats; -1 = unlimited, null = not configured */
        seats?: number | null;
      }>;
      currency: string;
      testMode?: boolean;
      faq: Array<{ question: string; answer: string }>;
    }>('GET', '/v2/pricing?product_family=tl4c');
  },

  /** GET /v2/pricing/promo — free trial length in days (public, no auth). */
  getPricingPromo() {
    return request<{
      trialDays: number;
    }>('GET', '/v2/pricing/promo');
  },

  createCheckout(
    authToken: string,
    payload: {
      /** Stripe price ID. Optional when `tier` is provided — the backend resolves prices via env-var fallbacks. */
      priceId?: string;
      interval?: 'month' | 'year';
      tier?: string;
      /** Stripe will redirect here on success; include {CHECKOUT_SESSION_ID} to verify purchase. */
      successUrl?: string;
      /** Stripe will redirect here if the user cancels checkout. */
      cancelUrl?: string;
    }
  ) {
    return request<{
      checkoutUrl: string;
    }>('POST', '/v2/billing/checkout', {
      token: authToken,
      body: payload,
    });
  },

  /**
   * Create a Stripe Elements subscription checkout for an authenticated user.
   * Returns clientSecret + subscriptionId + intentType so the frontend can
   * mount PaymentElement without an iframe or redirect to stripe.com.
   *
   * Response is either an Elements checkout object OR a portal redirect when the
   * caller already has a higher-tier active subscription (downgrade via portal).
   */
  createEmbeddedCheckout(
    authToken: string | undefined,
    payload: {
      tier?: string;
      priceId?: string;
      interval?: 'month' | 'year';
      orgName?: string;
      returnUrl?: string;
    }
  ) {
    return request<
      | { clientSecret: string; subscriptionId: string; intentType: 'setup' | 'payment' | 'none' }
      | { portalUrl: string; error: string; code: string; message: string }
    >('POST', '/v2/billing/checkout', {
      token: authToken || undefined,
      body: { ...payload, embedded: true },
    });
  },

  /**
   * POST /v2/billing/guest-checkout — Create a Stripe Elements checkout for an
   * unauthenticated guest. Returns a clientSecret for the PaymentElement.
   *
   * On success: `{ clientSecret, subscriptionId, intentType, checkoutToken }` — render Elements.
   * On existing account: `{ error, code, message, loginUrl }` — send magic link.
   */
  guestCheckout(payload: {
    tier?: string;
    priceId?: string;
    interval?: 'month' | 'year';
    email: string;
    name?: string;
    orgName?: string;
    couponCode?: string;
    eulaAcceptedAt?: string;
    eulaGitHash?: string;
    marketingOptIn?: boolean;
    referrer?: string;
  }) {
    return request<
      | { clientSecret: string; subscriptionId: string; intentType: 'payment' | 'setup' | 'none'; checkoutToken: string }
      | { error: string; code: string; message: string; loginUrl: string }
    >('POST', '/v2/billing/guest-checkout', {
      body: payload,
    });
  },

  /**
   * POST /v2/billing/guest-provision — provision a Guardrail account post-payment.
   * Accepts either subscriptionId (Elements flow) or sessionId (legacy).
   * Creates the account if needed and sends a magic-link email.
   * @param orgName - optional org/workspace name to set on the new account
   */
  guestProvision(
    id: string,
    idType: 'subscriptionId' | 'sessionId' = 'subscriptionId',
    orgName?: string
  ) {
    // Forward EULA + marketing opt-in from localStorage for account provisioning
    let eulaAcceptedAt: string | undefined;
    let eulaGitHash: string | undefined;
    let marketingOptIn: boolean | undefined;
    try {
      const stored = localStorage.getItem('guardrail_eula_accepted');
      if (stored) {
        const parsed = JSON.parse(stored);
        eulaAcceptedAt = parsed.acceptedAt;
        eulaGitHash = parsed.gitHash;
        marketingOptIn = parsed.marketingOptIn;
      }
    } catch { /* localStorage unavailable */ }

    return request<{
      provisioned: boolean;
      email?: string;
      authenticated?: boolean;
      access_token?: string;
      refresh_token?: string;
      expires_in?: number;
    }>(
      'POST',
      '/v2/billing/guest-provision',
      {
        body: {
          [idType]: id,
          ...(orgName ? { orgName } : {}),
          ...(eulaAcceptedAt ? { eulaAcceptedAt, eulaGitHash } : {}),
          ...(marketingOptIn !== undefined ? { marketingOptIn } : {}),
        },
      }
    );
  },

  /**
   * POST /v2/billing/apply-coupon — validate and apply a Stripe promotion code to an existing subscription.
   * Public: requires a signed checkoutToken issued at guest-checkout time.
   * @param subscriptionId - Stripe subscription ID (sub_…)
   * @param promotionCode  - Human-readable promo code string (e.g. "SUMMER40")
   * @param checkoutToken  - HMAC-signed token from the guest-checkout response
   */
  applyCoupon(subscriptionId: string, promotionCode: string, checkoutToken: string) {
    return request<{
      applied: boolean;
      discount: { type: string; value?: number; currency?: string; label: string };
      name: string;
    }>('POST', '/v2/billing/apply-coupon', {
      body: { subscriptionId, promotionCode, checkoutToken },
    });
  },

  // EULA acceptance
  acceptEula(gitHash: string, authToken?: string) {
    const token = authToken || getStoredToken();
    return request<{ accepted: boolean; acceptedAt: string; gitHash: string }>(
      'POST',
      '/v2/eula/accept',
      { body: { gitHash }, ...(token ? { token } : {}) }
    );
  },

  getEulaStatus(authToken?: string) {
    const token = authToken || getStoredToken();
    return request<{ accepted: boolean; acceptedAt?: string; gitHash?: string }>(
      'GET',
      '/v2/eula/status',
      { ...(token ? { token } : {}) }
    );
  },

  /** GET /v2/billing/subscription — live subscription details from KV */
  getSubscription(authToken: string) {
    return request<{
      status: string;
      tier: string;
      tierName: string;
      currentPeriodStart: string;
      currentPeriodEnd: string;
      cancelAtPeriodEnd: boolean;
      trialEnd?: string;
      /** Seat count from product.metadata.seats; -1 = unlimited, null = not set */
      seats?: number | null;
      /** Next payment after trial/period ends */
      upcomingInvoice?: {
        amountDue: number;
        dueDate?: string;
      };
      /** All billing-active subscriptions when user holds more than one plan. */
      subscriptions?: {
        id: string;
        tier: string;
        tierName: string;
        status: string;
        periodEnd: string;
        seats: number | null;
      }[];
    }>('GET', '/v2/billing/subscription', { token: authToken });
  },

  /**
   * POST /v2/billing/subscription/active — switch the active subscription.
   * Used when a user holds multiple active plans and wants to change tier context.
   */
  switchActiveSubscription(authToken: string, subscriptionId: string) {
    return request<{
      success: boolean;
      subscriptionId: string;
      tier: string;
      tierName: string;
      message: string;
    }>('POST', '/v2/billing/subscription/active', { token: authToken, body: { subscriptionId } });
  },

  /** POST /v2/billing/sync — reconcile latest Stripe subscription state for the signed-in user. */
  syncSubscription(authToken: string) {
    return request<{ synced: boolean; subscriptions?: number; message: string }>(
      'POST',
      '/v2/billing/sync',
      { token: authToken, body: {} }
    );
  },

  /** GET /v2/billing/portal — returns Stripe Customer Portal URL for self-service billing */
  getBillingPortal(authToken: string, returnUrl?: string) {
    const qs = returnUrl ? `?return_url=${encodeURIComponent(returnUrl)}` : '';
    return request<{ url: string }>('GET', `/v2/billing/portal${qs}`, { token: authToken });
  },

  /** POST /v2/billing/downgrade — returns Stripe Customer Portal URL for plan downgrades */
  getDowngradePortal(authToken: string, returnUrl?: string) {
    const qs = returnUrl ? `?return_url=${encodeURIComponent(returnUrl)}` : '';
    return request<{ portalUrl: string }>('POST', `/v2/billing/downgrade${qs}`, {
      token: authToken,
    });
  },

  /** POST /v2/billing/cancel — cancel subscription at period end */
  cancelSubscription(authToken: string) {
    return request<{ success: boolean; message: string; cancelAt: string }>(
      'POST',
      '/v2/billing/cancel',
      { token: authToken, body: {} }
    );
  },

  /**
   * POST /v2/billing/cancel/immediate — immediately cancel subscription (sandbox only).
   *
   * Only available when the API is running with a Stripe test key (sk_test_*).
   * Cancels the subscription right now and replays the deleted webhook event
   * locally so KV and Keycloak are updated without a running stripe-cli listener.
   */
  cancelSubscriptionImmediately(authToken: string) {
    return request<{ success: boolean; message: string; subscriptionId: string }>(
      'POST',
      '/v2/billing/cancel/immediate',
      { token: authToken, body: {} }
    );
  },

  onboard(name: string, email: string, company?: string, referrer?: string) {
    // Include EULA acceptance + marketing opt-in from localStorage (guest flow).
    let eulaAcceptedAt: string | undefined;
    let eulaGitHash: string | undefined;
    let marketingOptIn: boolean | undefined;
    try {
      const stored = localStorage.getItem('guardrail_eula_accepted');
      if (stored) {
        const parsed = JSON.parse(stored);
        eulaAcceptedAt = parsed.acceptedAt;
        eulaGitHash = parsed.gitHash;
        marketingOptIn = parsed.marketingOptIn;
      }
    } catch { /* localStorage unavailable */ }

    // Resolve referral code: prefer explicit param, fall back to localStorage.
    const resolvedReferrer = referrer || (() => {
      try { return localStorage.getItem('guardrail_referral_code') || undefined; }
      catch { return undefined; }
    })();

    return request<{
      success: boolean;
      apiKey?: string;
      stripeCustomerId: string;
      tier: string;
      scansRemaining: number;
      trialEndsAt?: string;
      message: string;
      nextSteps?: string[];
      existing?: boolean;
    }>('POST', '/v2/onboard', {
      body: {
        name,
        email,
        company,
        ...(eulaAcceptedAt ? { eulaAcceptedAt, eulaGitHash } : {}),
        ...(marketingOptIn !== undefined ? { marketingOptIn } : {}),
        ...(resolvedReferrer ? { referrer: resolvedReferrer } : {}),
      },
    });
  },

  /** POST /v2/auth/magic-link — send a sign-in link to an existing account. */
  sendMagicLink(email: string) {
    return request<{ success: boolean; userFound?: boolean; message: string }>(
      'POST',
      '/v2/auth/magic-link',
      { body: { email } }
    );
  },

  /**
   * Exchange a Keycloak authorization code for JWT tokens (PKCE flow).
   * Called by AuthCallbackPage after KC redirects to /auth/callback?code=...
   */
  exchangeAuthCode(code: string, redirectUri: string, codeVerifier?: string) {
    return request<{
      success: boolean;
      access_token: string;
      refresh_token?: string;
      expires_in?: number;
      token_type?: string;
    }>('POST', '/v2/auth/exchange-code', {
      body: {
        code,
        redirect_uri: redirectUri,
        ...(codeVerifier ? { code_verifier: codeVerifier } : {}),
      },
    });
  },

  // ---------------------------------------------------------------------------
  // API Key Management  (/v2/keys)
  // ---------------------------------------------------------------------------

  /** GET /v2/keys — list user's API keys (masked prefixes only) */
  listKeys(authToken: string) {
    return request<{
      keys: Array<{
        id: string;
        name: string;
        prefix: string;
        createdAt: string;
        lastUsedAt?: string;
        expiresAt?: string;
      }>;
      limit: number;
      canCreateMore: boolean;
    }>('GET', '/v2/keys', { token: authToken });
  },

  /** POST /v2/keys — generate a new API key (full key shown once in response) */
  createKey(authToken: string, body: { name?: string; expiresInDays?: number }) {
    return request<{
      id: string;
      name: string;
      key: string;
      prefix: string;
      createdAt: string;
      expiresAt?: string;
      message: string;
    }>('POST', '/v2/keys', { token: authToken, body });
  },

  /** POST /v2/keys/:id/rotate — generate a replacement key, revoke the old one */
  rotateKey(authToken: string, keyId: string) {
    return request<{
      id: string;
      name: string;
      key: string;
      prefix: string;
      createdAt: string;
      expiresAt?: string;
      message: string;
    }>('POST', `/v2/keys/${keyId}/rotate`, { token: authToken, body: {} });
  },

  /** DELETE /v2/keys/:id — permanently revoke an API key */
  revokeKey(authToken: string, keyId: string) {
    return request<{ success: boolean; message: string; keyId: string }>(
      'DELETE',
      `/v2/keys/${keyId}`,
      { token: authToken }
    );
  },

  getScan(scanId: string, authToken?: string) {
    return request<{
      scanId: string;
      status: 'queued' | 'running' | 'completed' | 'failed';
    }>('GET', `/v2/scan/${scanId}`, { token: authToken ?? getStoredToken() });
  },

  getScanHistory(limit: number, authToken?: string) {
    return request<{
      scans: unknown[];
      data?: unknown[];
    }>('GET', `/v2/scan/history?limit=${limit}`, { token: authToken ?? getStoredToken() });
  },

  /** Full scan result for report pages — returns raw API shape without narrowing. */
  getScanFull(scanId: string, authToken?: string) {
    return request<Record<string, unknown>>('GET', `/v2/scan/${scanId}`, {
      token: authToken ?? getStoredToken(),
    });
  },

  /** Latest scan(s) for report pages — returns raw API shape without narrowing. */
  getScanHistoryFull(limit: number, authToken?: string) {
    return request<Record<string, unknown>>('GET', `/v2/scan/history?limit=${limit}`, {
      token: authToken ?? getStoredToken(),
    });
  },

  // ---------------------------------------------------------------------------
  // Admin — Feature Flag Management
  // ---------------------------------------------------------------------------

  /**
   * List all boolean feature flags with their current override state.
   * Each flag also includes the Stripe product metadata key for sync reference.
   * Requires Keycloak 'admin' realm role.
   */
  getFeatureFlags(authToken: string) {
    return request<{
      flags: Array<{
        name: string;
        displayName: string;
        description: string;
        /** Stripe product metadata key — e.g. "feature_complianceCheck" */
        stripeMetadataKey: string;
        hasOverride: boolean;
        globalKillSwitch: boolean;
        tierOverrides: Partial<Record<string, boolean>> | null;
        updatedAt: string | null;
        updatedBy: string | null;
      }>;
      total: number;
    }>('GET', '/v2/admin/features', { token: authToken });
  },

  /**
   * Upsert a feature flag override.
   * Omitted fields are preserved for existing records (partial update).
   * Requires Keycloak 'admin' realm role.
   *
   * @param name - BooleanFeatureKey (e.g. 'complianceCheck')
   */
  setFeatureFlag(
    authToken: string,
    name: string,
    body: { globalKillSwitch?: boolean; tierOverrides?: Partial<Record<string, boolean>> }
  ) {
    return request<{ flag: unknown; message: string }>('PUT', `/v2/admin/features/${name}`, {
      token: authToken,
      body,
    });
  },

  /**
   * Remove a feature flag override, reverting the feature to static tier-config.
   * Requires Keycloak 'admin' realm role.
   *
   * @param name - BooleanFeatureKey (e.g. 'complianceCheck')
   */
  deleteFeatureFlag(authToken: string, name: string) {
    return request<{ deleted: boolean; name: string; message: string }>(
      'DELETE',
      `/v2/admin/features/${name}`,
      { token: authToken }
    );
  },

  // ---------------------------------------------------------------------------
  // Organizations
  // ---------------------------------------------------------------------------

  /** GET /v2/organizations — list orgs the caller belongs to */
  listOrganizations(authToken: string) {
    return request<{
      success: boolean;
      /** The org ID this user owns (null = user does not own any org). */
      ownedOrgId: string | null;
      organizations: Array<{
        id: string;
        name: string;
        displayName?: string;
        description?: string;
        enabled?: boolean;
      }>;
    }>('GET', '/v2/organizations', { token: authToken });
  },

  /** GET /v2/organizations/:id — org details + members + seat quota */
  getOrganization(authToken: string, orgId: string) {
    return request<{
      success: boolean;
      organization: {
        id: string;
        name: string;
        displayName?: string;
        description?: string;
        seatsTotal: number;
        seatsUsed: number;
        seatsAvailable: number;
        members: Array<{
          id: string;
          username: string;
          email: string;
          firstName?: string;
          lastName?: string;
        }>;
      };
    }>('GET', `/v2/organizations/${orgId}`, { token: authToken });
  },

  /** POST /v2/organizations — create org */
  createOrganization(
    authToken: string,
    body: { name: string; displayName?: string; description?: string }
  ) {
    return request<{ success: boolean; id: string; name: string; message: string }>(
      'POST',
      '/v2/organizations',
      { token: authToken, body }
    );
  },

  /** POST /v2/organizations/:id/members — add existing user by email or userId */
  addOrgMember(authToken: string, orgId: string, body: { email?: string; userId?: string }) {
    return request<{ success: boolean; message: string }>(
      'POST',
      `/v2/organizations/${orgId}/members`,
      { token: authToken, body }
    );
  },

  /** DELETE /v2/organizations/:id/members/:userId — remove member */
  removeOrgMember(authToken: string, orgId: string, userId: string) {
    return request<{ success: boolean; message: string }>(
      'DELETE',
      `/v2/organizations/${orgId}/members/${userId}`,
      { token: authToken }
    );
  },

  /** DELETE /v2/organizations/:id — delete org (member or admin) */
  deleteOrganization(authToken: string, orgId: string) {
    return request<{ success: boolean; message: string }>('DELETE', `/v2/organizations/${orgId}`, {
      token: authToken,
    });
  },

  /** POST /v2/organizations/:id/invite — invite user by email (creates KC account if needed) */
  inviteOrgMember(
    authToken: string,
    orgId: string,
    body: { email: string; firstName?: string; lastName?: string; redirectUri?: string }
  ) {
    return request<{ success: boolean; message: string; isNewUser: boolean }>(
      'POST',
      `/v2/organizations/${orgId}/invite`,
      { token: authToken, body }
    );
  },

  /** POST /v2/organizations/:id/subscription — link caller's subscription to org */
  linkOrgSubscription(authToken: string, orgId: string) {
    return request<{
      success: boolean;
      message: string;
      orgId: string;
      tier: string;
      teamSeats: number;
    }>('POST', `/v2/organizations/${orgId}/subscription`, { token: authToken, body: {} });
  },

  /** GET /v2/organizations/:id/subscription — get org subscription details */
  getOrgSubscription(authToken: string, orgId: string) {
    return request<{
      success: boolean;
      subscription: {
        tier: string;
        tierName: string;
        teamSeats: number;
        seatsUsed: number;
        seatsAvailable: number;
        scansRemaining: number;
        subscriptionStatus: string;
        periodEnd: string;
      } | null;
    }>('GET', `/v2/organizations/${orgId}/subscription`, { token: authToken });
  },

  scan(
    targetUrl: string,
    type: string,
    depth: 'quick' | 'standard' | 'deep',
    authToken: string,
    githubToken?: string,
    options?: {
      includeAiBom?: boolean;
      includeDependencies?: boolean;
      includeLicenseAnalysis?: boolean;
      includeVendorGuard?: boolean;
      includeThreatModel?: boolean;
      includeCompliance?: boolean;
    },
    extraHeaders?: Record<string, string>,
    dynamicAnalysis?: {
      targetEndpoint: string;
      probes: string[];
      authToken?: string;
      timeoutMs?: number;
    },
    aiAnalysis?: {
      analyzers?: string[];
    },
    branch?: string,
    /** Opt-in Zero-Day Engine analysis (requires paid subscription). */
    zdeAnalysis?: boolean,
    /** Opt-in Rules File Integrity Analysis (requires paid subscription). */
    rfiaEnabled?: boolean,
    /** Opt-in Invariant Vault analysis (SLM + knowledge base, requires paid subscription). */
    invariantAnalysis?: boolean,
    /** Per-engine selection for ZDE (when zdeAnalysis is true). */
    zdeEngines?: Record<string, boolean>,
    /** GRTTL4C-125: Local file scan — array of {path, content} pairs from browser file picker.
     *  When provided, target should be set to 'local:<foldername>'. */
    files?: Array<{ path: string; content: string }>,
  ) {
    return request<{
      scanId: string;
      status: 'queued' | 'running' | 'completed' | 'failed';
      target: string;
      type: string;
      createdAt: string;
      estimatedDuration?: number;
      resultUrl?: string;
    }>('POST', '/v2/scan', {
      token: authToken,
      body: {
        target: targetUrl,
        type,
        depth,
        githubToken,
        options,
        ...(dynamicAnalysis ? { dynamicAnalysis } : {}),
        ...(aiAnalysis ? { aiAnalysis } : {}),
        ...(branch ? { branch } : {}),
        ...(zdeAnalysis ? { zdeAnalysis: true } : {}),
        ...(rfiaEnabled ? { rfiaEnabled: true } : {}),
        ...(invariantAnalysis ? { invariantAnalysis: true } : {}),
        ...(zdeEngines ? { zdeEngines } : {}),
        ...(files && files.length > 0 ? { files } : {}),
      },
      extraHeaders,
    });
  },
};
